/**
 * Main Application Component
 */
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TabAccountWatch } from '@/components/auth/TabAccountWatch'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { AdminGuard } from '@/components/auth/AdminGuard'
import { ManagerRouteGuard } from '@/components/auth/ManagerRouteGuard'
import { RegistrationGate } from '@/components/auth/RegistrationGate'
import { DashboardLayout } from '@/components/layout'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { Dashboard } from '@/pages/Dashboard'
import { DataSourcesPage } from '@/pages/DataSourcesPage'
import { UsersPage } from '@/pages/UsersPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { DataSourceDetailPage } from '@/pages/DataSourceDetailPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { ServiceManagementPage } from '@/pages/ServiceManagementPage'
import { MergedDocumentPage } from '@/pages/MergedDocumentPage'
import { ClientsPage } from '@/pages/ClientsPage'
import { ClientDetailPage } from '@/pages/ClientDetailPage'
import { ClientCompaniesPage } from '@/pages/ClientCompaniesPage'
import { ClientCompanyDetailPage } from '@/pages/ClientCompanyDetailPage'
import { ManagersPage } from '@/pages/ManagersPage'
import { ManagerDetailPage } from '@/pages/ManagerDetailPage'
import { UserDetailPage } from '@/pages/UserDetailPage'
import { ContactChatPage } from '@/pages/ContactChatPage'
import { EstimateDetailPage } from '@/pages/EstimateDetailPage'
import { LEGACY_ESTIMATE_PATHS } from '@/lib/legacyPaths'
import { FunnelPage } from '@/pages/FunnelPage'
import { EmailsPage } from '@/pages/EmailsPage'
import { CatalogPage } from '@/pages/CatalogPage'
import { CatalogProductPage } from '@/pages/CatalogProductPage'
import { OrdersPage } from '@/pages/OrdersPage'
import { DiscountsPage } from '@/pages/DiscountsPage'
import { CrmPage } from '@/pages/CrmPage'
import { LeadTemperaturePage } from '@/pages/LeadTemperaturePage'
import { HelpPage } from '@/pages/HelpPage'
import { OrderDetailPage } from '@/pages/OrderDetailPage'
import { DatasetDetailPage } from '@/pages/DatasetDetailPage'
import { DatasetUploadPage } from '@/pages/DatasetUploadPage'
import { BillingPage } from '@/pages/BillingPage'
import { ExpensesPage } from '@/pages/ExpensesPage'
import { BalancePage } from '@/pages/BalancePage'
import { PaymentSuccessPage } from '@/pages/PaymentSuccessPage'
import { MobileLayout } from '@/components/mobile/MobileLayout'
import { MobileSlashRedirect } from '@/components/mobile/MobileSlashRedirect'
import { MobileLoginPage } from '@/pages/mobile/MobileLoginPage'
import { MobileLoginExpiredPage } from '@/pages/mobile/MobileLoginExpiredPage'
import { MobilePasswordLoginPage } from '@/pages/mobile/MobilePasswordLoginPage'
import { MobileAttentionPage } from '@/pages/mobile/MobileAttentionPage'
import { MobileLeadsPage } from '@/pages/mobile/MobileLeadsPage'
import { MobileBoardPage } from '@/pages/mobile/MobileBoardPage'
import { MobileMeetingsPage } from '@/pages/mobile/MobileMeetingsPage'
import { MobileProfilePage } from '@/pages/mobile/MobileProfilePage'
import { MobileClientPage } from '@/pages/mobile/MobileClientPage'
import { MobileCompanyPage } from '@/pages/mobile/MobileCompanyPage'
import { MobileStartPage } from '@/pages/mobile/MobileStartPage'
import { AppToaster } from '@/components/AppToaster'
import { FeatureGuard } from '@/components/billing/FeatureGuard'
import { SuperuserGuard } from '@/components/auth/SuperuserGuard'
import { PlatformPage } from '@/pages/PlatformPage'

/**
 * Legacy /datasets/:id[/upload] → /clients/cold-calls/:id[/upload] redirect
 * (TASK-337, AC-3): user bookmarks keep working after the sections merged.
 */
function LegacyDatasetRedirect({ suffix = '' }: { suffix?: string }): JSX.Element {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/clients/cold-calls/${id}${suffix}`} replace />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* KAN-925: аккаунт вкладки вышел в другой вкладке: перезагрузка */}
        <TabAccountWatch />
        <BrowserRouter>
          <Routes>
            {/* Public Routes (no auth required) */}
            <Route path="/login" element={<LoginPage />} />
            {/* KAN-684: при закрытой регистрации редирект на /login с сообщением */}
            <Route path="/register" element={<RegistrationGate><RegisterPage /></RegistrationGate>} />
            <Route path="/verify-email/:uidb64/:token" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:uidb64/:token" element={<ResetPasswordPage />} />
            <Route path="/accept-invite/:uid/:token" element={<AcceptInvitePage />} />

            {/* KAN-834: приложение менеджера (PWA): вход по ссылке и свои экраны вне кабинета */}
            <Route path="/m/login" element={<MobilePasswordLoginPage />} />
            <Route path="/m/login/:uid/:token" element={<MobileLoginPage />} />
            <Route path="/m/login-expired" element={<MobileLoginExpiredPage />} />
            {/* KAN-845: "Начало работы" во весь экран, без вкладок */}
            {/* KAN-988: экраны после входа только при функции тарифа manager_app; вход выше не закрыт */}
            <Route path="/m/start" element={<AuthGuard><ManagerRouteGuard><FeatureGuard feature="manager_app"><MobileStartPage /></FeatureGuard></ManagerRouteGuard></AuthGuard>} />
            <Route element={<AuthGuard><ManagerRouteGuard><FeatureGuard feature="manager_app"><MobileLayout /></FeatureGuard></ManagerRouteGuard></AuthGuard>}>
              <Route path="/m" element={<MobileSlashRedirect><MobileAttentionPage /></MobileSlashRedirect>} />
              <Route path="/m/leads" element={<MobileLeadsPage />} />
              {/* KAN-964: доска без вкладки (вкладка "Встречи"), адрес жив для ссылок */}
              <Route path="/m/board" element={<MobileBoardPage />} />
              <Route path="/m/meetings" element={<MobileMeetingsPage />} />
              <Route path="/m/profile" element={<MobileProfilePage />} />
              <Route path="/m/clients/:id" element={<MobileClientPage />} />
              {/* KAN-851: карточка организации клиента (только чтение) */}
              <Route path="/m/companies/:id" element={<MobileCompanyPage />} />
            </Route>

            {/* Dashboard Layout Routes (protected); KAN-811: менеджер только в разрешённых разделах */}
            <Route element={<AuthGuard><ManagerRouteGuard><DashboardLayout /></ManagerRouteGuard></AuthGuard>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/data-sources" element={<AdminGuard><DataSourcesPage /></AdminGuard>} />
              <Route path="/data-sources/:id" element={<AdminGuard><DataSourceDetailPage /></AdminGuard>} />
              <Route path="/users" element={<UsersPage />} />
              {/* KAN-752: страница оператора вместо модалки редактирования */}
              <Route path="/users/:id" element={<UserDetailPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/services" element={<ServiceManagementPage />} />
              <Route path="/merged-document" element={<MergedDocumentPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              {/* cold-calls BEFORE the parametric /clients/:id (FR-002 route order) */}
              <Route path="/clients/cold-calls" element={<ClientsPage />} />
              <Route path="/clients/cold-calls/:id" element={<DatasetDetailPage />} />
              <Route path="/clients/cold-calls/:id/upload" element={<DatasetUploadPage />} />
              {/* KAN-736: "Широкий вид" of the conversation modal */}
              <Route path="/clients/:clientId/chat" element={<ContactChatPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              {/* KAN-826: организации клиентов */}
              <Route path="/companies" element={<ClientCompaniesPage />} />
              <Route path="/companies/:id" element={<ClientCompanyDetailPage />} />
              <Route path="/funnel" element={<FunnelPage />} />
              {/* KAN-744: менеджеры (живые люди, доводят клиентов до продажи) */}
              <Route path="/managers" element={<ManagersPage />} />
              <Route path="/managers/:id" element={<ManagerDetailPage />} />
              {/* KAN-718: журнал писем организации */}
              <Route path="/emails" element={<EmailsPage />} />
              {/* KAN-743: каталог товаров интернет-магазина (только просмотр) */}
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/products/:id" element={<CatalogProductPage />} />
              {/* KAN-755: скидки и группы товаров */}
              <Route path="/discounts" element={<DiscountsPage />} />
              {/* KAN-746: заказы интернет-магазина */}
              <Route path="/orders" element={<OrdersPage />} />
              {/* KAN-988: CRM и лиды по теплоте только при функции тарифа crm */}
              <Route path="/crm" element={<FeatureGuard feature="crm"><CrmPage /></FeatureGuard>} />
              {/* KAN-810: лиды по теплоте (горячие, тёплые, прохладные) */}
              <Route path="/leads" element={<FeatureGuard feature="crm"><LeadTemperaturePage /></FeatureGuard>} />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              {/* KAN-951: раздел "Помощь": видео и текст по разделам кабинета */}
              <Route path="/help" element={<HelpPage />} />
              <Route path="/help/:slug" element={<HelpPage />} />
              {/* Legacy campaign routes → redirects (TASK-337, AC-3) */}
              <Route path="/datasets" element={<Navigate to="/clients/cold-calls" replace />} />
              <Route path="/datasets/:id" element={<LegacyDatasetRedirect />} />
              <Route path="/datasets/:id/upload" element={<LegacyDatasetRedirect suffix="/upload" />} />
              {/* Телефония и Telegram-аккаунты стали вкладками Настроек; старые адреса ведут туда */}
              <Route path="/telephony" element={<Navigate to="/settings?tab=telephony" replace />} />
              <Route path="/telegram-accounts" element={<Navigate to="/settings?tab=telegram" replace />} />
              {/* KAN-341: биллинг — только владельцу: суммы, история платежей
                  и отмена тарифа не для каждого сотрудника организации.
                  /billing/success — куда шлюз возвращает после оплаты; раньше
                  такого маршрута не было и оплата заканчивалась на 404. */}
              <Route path="/billing" element={<AdminGuard><BillingPage /></AdminGuard>} />
              <Route path="/billing/success" element={<AdminGuard><PaymentSuccessPage /></AdminGuard>} />
              {/* KAN-735: расходы организации, только владельцу */}
              <Route path="/expenses" element={<AdminGuard><ExpensesPage /></AdminGuard>} />
              {/* KAN-976: кошелёк кабинета в рублях */}
              <Route path="/balance" element={<AdminGuard><BalancePage /></AdminGuard>} />
              {/* KAN-987: страницы смет убраны, старые ссылки ведут на дашборд */}
              {LEGACY_ESTIMATE_PATHS.map((path) => (
                <Route key={path} path={path} element={<Navigate to="/" replace />} />
              ))}
              <Route path="/estimates/:id" element={<EstimateDetailPage />} />
              {/* KAN-980 / KAN-979: раздел платформы, только суперпользователю */}
              <Route path="/platform" element={<SuperuserGuard><PlatformPage /></SuperuserGuard>} />
            </Route>

            {/* Catch-all 404 route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <AppToaster />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
